// Hooks
import { useCallback, useEffect, useState, startTransition } from "react";
import { useMedplum } from "@medplum/react";

// Types
import type { MedplumClient } from "@medplum/core";
import type { Coverage, Patient } from "@medplum/fhirtypes";

// Events
import { DomainEventNames } from "@benchmark/core/events";
import { useEventDrivenRefetch } from "@benchmark/core/hooks";

/**
 * Code for Coverage.class rows that describe the plan tier (FHIR coverage-class).
 * @agentic @benchmark/core
 */
const COVERAGE_CLASS_PLAN_CODE = "plan";

/**
 * Insurance coverage display info for primary or secondary.
 * @agentic @benchmark/core
 */
export type CoverageInfo = {
  insuranceInfo: string;
  memberNumber: string;
};

/**
 * Result type for usePatientCoverage hook.
 * @agentic @benchmark/core
 */
export type PatientCoverageResult = {
  primary: CoverageInfo;
  secondary: CoverageInfo | null;
  loading: boolean;
  error: Error | null;
};

/**
 * Returns true when a Coverage.class row includes a plan-type coding.
 * @agentic @benchmark/core
 */
function classRowIsPlanType(
  row: NonNullable<Coverage["class"]>[number],
): boolean {
  return (
    row.type?.coding?.some((c) => c.code === COVERAGE_CLASS_PLAN_CODE) === true
  );
}

/**
 * Extracts the best class row label from Coverage.class (plan name first).
 * @agentic @benchmark/core
 */
function getClassDisplayNameFromCoverage(
  coverage: Coverage,
): string | undefined {
  const rows = coverage.class;
  if (!rows?.length) {
    return undefined;
  }

  const planNamed = rows.find(
    (row) => classRowIsPlanType(row) && row.name?.trim(),
  );
  if (planNamed?.name?.trim()) {
    return planNamed.name.trim();
  }

  const anyNamed = rows.find((row) => row.name?.trim());
  return anyNamed?.name?.trim();
}

/**
 * Returns the first trim-non-empty payor Reference.display on the Coverage.
 * @agentic @benchmark/core
 */
function getPayorDisplayFromCoverage(coverage: Coverage): string | undefined {
  for (const ref of coverage.payor ?? []) {
    const display = ref.display?.trim();
    if (display) {
      return display;
    }
  }
  return undefined;
}

/**
 * Builds display strings from Coverage only (no related resource reads).
 * @agentic @benchmark/core
 */
export function buildCoverageInfoFromCoverage(
  coverage: Coverage,
): CoverageInfo {
  const classDisplay = getClassDisplayNameFromCoverage(coverage);
  const payorDisplay = getPayorDisplayFromCoverage(coverage);
  const insuranceInfo = classDisplay || payorDisplay || "Insurance";
  return {
    insuranceInfo,
    memberNumber: coverage.subscriberId?.trim() ?? "",
  };
}

/**
 * Sorts active coverages by FHIR applicability: lower `order` first, then id.
 * @agentic @benchmark/core
 */
export function sortActiveCoveragesByApplicabilityOrder(
  coverages: Coverage[],
): Coverage[] {
  return [...coverages].sort((a, b) => {
    const orderA = a.order ?? Number.POSITIVE_INFINITY;
    const orderB = b.order ?? Number.POSITIVE_INFINITY;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    const idA = a.id ?? "";
    const idB = b.id ?? "";
    return idA.localeCompare(idB);
  });
}

/**
 * Fetches patient Coverage, ranks by `order`, derives labels from Coverage fields only.
 * @agentic @benchmark/core
 */
export function usePatientCoverage(patient: Patient): PatientCoverageResult {
  const medplum = useMedplum();
  const [primary, setPrimary] = useState<CoverageInfo>({
    insuranceInfo: "",
    memberNumber: "",
  });
  const [secondary, setSecondary] = useState<CoverageInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!patient.id) {
      startTransition(() => {
        setPrimary({ insuranceInfo: "", memberNumber: "" });
        setSecondary(null);
      });
      return;
    }

    let cancelled = false;
    startTransition(() => {
      setLoading(true);
      setError(null);
    });

    const searchCoverages = async (): Promise<Coverage[]> => {
      const medplumWithSearchResources = medplum as MedplumClient & {
        searchResources?: (
          resourceType: "Coverage",
          query: { patient: string },
        ) => Promise<Coverage[]>;
      };
      if (typeof medplumWithSearchResources.searchResources === "function") {
        return medplumWithSearchResources.searchResources("Coverage", {
          patient: patient.id!,
        });
      }

      const medplumWithSearch = medplum as MedplumClient & {
        search?: (
          resourceType: "Coverage",
          query: { patient: string },
        ) => Promise<{
          entry?: Array<{ resource?: Coverage }>;
        }>;
      };
      if (typeof medplumWithSearch.search === "function") {
        const bundle = await medplumWithSearch.search("Coverage", {
          patient: patient.id!,
        });
        return (
          bundle.entry
            ?.map((entry) => entry.resource)
            .filter((coverage): coverage is Coverage => !!coverage) ?? []
        );
      }

      throw new Error("Medplum client missing search method for Coverage");
    };

    void searchCoverages()
      .then((coverages) => {
        if (cancelled) {
          return;
        }

        const activeCoverages = (coverages || []).filter(
          (coverage) => coverage.status === "active",
        );

        if (activeCoverages.length === 0) {
          startTransition(() => {
            setPrimary({ insuranceInfo: "Self Pay", memberNumber: "" });
            setSecondary(null);
            setLoading(false);
          });
          return;
        }

        const sorted = sortActiveCoveragesByApplicabilityOrder(activeCoverages);
        const primaryCoverage = sorted[0];
        const secondaryCoverage = sorted[1];

        startTransition(() => {
          setPrimary(buildCoverageInfoFromCoverage(primaryCoverage));
          setSecondary(
            secondaryCoverage
              ? buildCoverageInfoFromCoverage(secondaryCoverage)
              : null,
          );
          setLoading(false);
        });
      })
      .catch(
        (
          caught: Error & { outcome?: { issue?: Array<{ code?: string }> } },
        ) => {
          if (cancelled) {
            return;
          }

          if (caught?.outcome?.issue?.[0]?.code !== "not-found") {
            console.error("Error fetching coverage:", caught);
            startTransition(() => {
              setError(caught);
            });
          }
          startTransition(() => {
            setLoading(false);
          });
        },
      );

    return () => {
      cancelled = true;
    };
  }, [patient.id, medplum, reloadKey]);

  // Auto-refetch on coverage events for this patient.
  const triggerReload = useCallback(() => setReloadKey((k) => k + 1), []);
  useEventDrivenRefetch(
    [DomainEventNames.CoverageCreated, DomainEventNames.CoverageUpdated],
    {
      refetch: triggerReload,
      enabled: !!patient.id,
      filter: (payload) =>
        (payload as { patientId?: string }).patientId === patient.id,
    },
  );

  return { primary, secondary, loading, error };
}
